import React, { useState, useMemo, useEffect } from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Grid,
    Typography,
    Card,
    CardContent,
    Tabs,
    Tab,
    TextField,
    MenuItem,
    Button,
    Chip,
    IconButton,
    Collapse,
    Stack,
    Divider,
    Paper,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import LaunchIcon from '@mui/icons-material/Launch';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useFitHeight } from '../../../_metronic/layout/Layout';
import { Portlet, PortletBody } from '../../partials/content/Portlet';
import { createFloatingPoint } from '../../util/format';
import { DataTile } from '../../common/widgets/DataTile';
import { Link, useHistory } from 'react-router-dom';
import { RootState } from '../../store/reducers';
import * as patternActions from '../../store/actions/patterns';
import { useGlobalStore, useDefaultFiltersState } from '../../common';

const useStyles = makeStyles((theme: any) => ({
    root: {
        padding: '0px',
        minHeight: '100%',
        backgroundColor: '#f3f6f9',
    },
    container: {
        padding: '24px',
        paddingRight: '10px',
        paddingLeft: '10px',
    },
    headerTitle: {
        fontSize: '20px',
        fontWeight: 700,
        marginBottom: '24px',
        color: '#181c32',
    },
    statsContainer: {
        marginBottom: '24px',
        backgroundColor: '#fff',
        padding: '20px 24px',
        width:'100%',
        margin:'unset !important'
    },
    statCard: {
        height: '100%',
        borderRadius: '10px',
        border: '1px solid #eff2f5',
        boxShadow: 'none !important',
        display: 'flex',
        flexDirection: 'column',
    },
    statLabel: {
        fontSize: '13px',
        fontWeight: 600,
        marginBottom: '12px',
    },
    statValue: {
        fontSize: '32px',
        fontWeight: 700,
        color: '#181c32',
    },
    tabsWrapper: {
        backgroundColor: '#fff',
        borderRadius: '10px 10px 0 0',
        borderBottom: '1px solid #eff2f5',
        marginTop: '24px',
    },
    tab: {
        fontWeight: 600,
    },
    filterBar: {
        backgroundColor: '#fff',
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        borderBottom: '1px solid #eff2f5',
    },
    filterField: {
        minWidth: '250px',
        '& .MuiInputLabel-root': {
            fontSize: '13px',
            color: '#7e8299',
            fontWeight: 500,
        },
        '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            backgroundColor: '#f5f8fa',
            border: 'none',
            '& fieldset': {
                border: 'none',
            },
            '&:hover fieldset': {
                border: 'none',
            },
            '&.Mui-focused fieldset': {
                border: 'none',
            },
        }
    },
    patternList: {
        marginTop: '24px',
    },
    patternCard: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        border: '1px solid #eff2f5',
        marginBottom: '16px',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
    },
    cardPadding: {
        padding: '20px 24px',
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
    },
    patternName: {
        fontSize: '18px',
        fontWeight: 700,
        color: '#181c32',
    },
    severityBadge: {
        borderRadius: '6px',
        fontWeight: 600,
        padding: '4px 12px',
        fontSize: '12px',
    },
    statRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '40px',
        marginBottom: '16px',
    },
    metric: {
        display: 'flex',
        alignItems: 'baseline',
        gap: '8px',
    },
    metricLabel: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#181c32',
    },
    metricValue: {
        fontSize: '14px',
        color: '#7e8299',
    },
    metricSecondary: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    secondaryLabel: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#181c32',
        position: 'relative',
        paddingLeft: '12px',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            backgroundColor: '#181c32',
        }
    },
    secondaryValue: {
        fontSize: '14px',
        color: '#7e8299',
    },
    viewDetails: {
        display: 'flex',
        alignItems: 'center',
        color: '#3699ff',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        gap: '4px',
        marginTop: '12px',
        width: 'fit-content',
        '&:hover': {
            color: '#187de4',
        }
    },
    expandContent: {
        padding: '20px 24px',
        backgroundColor: '#fff',
        borderTop: '1px solid #eff2f5',
    },
    descriptionText: {
        fontSize: '14px',
        color: '#3f4254',
        lineHeight: '1.6',
        marginBottom: '20px',
    },
    chipGroup: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
        marginBottom: '20px',
    },
    customChip: {
        borderRadius: '6px',
        fontWeight: 600,
        height: '28px',
        fontSize: '12px',
    },
    claimNumbersToggle: {
        display: 'flex',
        alignItems: 'center',
        color: '#3699ff',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        gap: '4px',
        width: 'fit-content',
    },
    claimNumbersGrid: {
        marginTop: '16px',
        paddingRight: '8px',
    },
    // Batch Data Profile Styles
    batchProfileRoot: {
        marginTop: '24px',
    },
    batchHeader: {
        fontSize: '13px',
        fontWeight: 700,
        color: '#5e6278',
        marginBottom: '16px',
        textTransform: 'uppercase',
    },
    batchCard: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        border: '1px solid #eff2f5',
        overflow: 'hidden',
    },
    batchCardHeader: {
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#f9f9f9',
        }
    },
    batchHeaderText: {
        fontSize: '14px',
        fontWeight: 700,
        color: '#3f4254',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    batchContent: {
        padding: '24px',
        borderTop: '1px solid #eff2f5',
    },
    profileGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '40px',
    },
    profileCol: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    profileRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    profileLabel: {
        fontSize: '14px',
        color: '#7e8299',
        fontWeight: 500,
    },
    profileValue: {
        fontSize: '14px',
        color: '#181c32',
        fontWeight: 700,
    },
    profileValueDim: {
        fontSize: '14px',
        color: '#7e8299',
        fontWeight: 500,
    },
    profileLabelMid: {
        fontSize: '14px',
        color: '#3699ff',
        fontWeight: 600,
    },
    profileValueNull: {
        fontSize: '14px',
        color: '#3f4254',
        fontWeight: 600,
    }
}));

const PatternIntelligencePage = () => {
    useFitHeight();
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const defaultFilters = useDefaultFiltersState();

    const [tabIndex, setTabIndex] = useState(0);
    const [sortBy, setSortBy] = useState('varianceRatioHighToLow');
    const [expandedDetails, setExpandedDetails] = useState<string | number | null>(null);
    const [expandedClaims, setExpandedClaims] = useState<string | number | null>(null);
    const [batchExpanded, setBatchExpanded] = useState(true);

    const handlePatternClick = (e, patternName) => {
        e.preventDefault();
        const baseFilters = defaultFilters();
        
        // Remove date filters and set paid/denial to 'both' to ensure we see all claims for this pattern
        const customFilter = (baseFilters.customFilter || []).filter(f => 
            f.field !== 'remitdate' && 
            f.field !== 'servicedate' && 
            f.field !== 'isPaid' && 
            f.field !== 'isDenial' && 
            !f.relativeFilter
        );
        
        customFilter.push({ field: 'isPaid', equator: 'equals', value: 'both' });
        customFilter.push({ field: 'isDenial', equator: 'equals', value: 'both' });

        const filters = {
            ...baseFilters,
            customFilter,
            filter: [
                {
                    field: 'ClaimPatternAssignment.PatternName',
                    equator: 'equals',
                    value: patternName
                }
            ]
        };
        sessionStorage.setItem('redirect-page-filters:claimAnalysis-landing', JSON.stringify(filters));
        history.push('/cms/claimAnalysis/fromSummary');
    };

    const { summary, list, details, loading } = useSelector((state: RootState) => state.patterns);

    useEffect(() => {
        dispatch(patternActions.fetchPatternSummary());
        dispatch(patternActions.fetchPatterns());
    }, [dispatch]);

    const data = useMemo(() => {
        const stats = [
            { label: 'Total Patterns Found', value: summary.patternCount, color: 'danger', labelPrefix: '', labelSuffix: '', isTwoDecimal: false },
            { label: 'Percentage of Claims Clustered', value: summary.clusteredPct, color: 'success', labelPrefix: '', labelSuffix: '%', isTwoDecimal: true },
            { label: 'Top Recovery Opportunity in Dollars', value: summary.topRecoveryDollars, color: 'info', labelPrefix: '$', labelSuffix: '', isTwoDecimal: true },
            { label: 'Outlier Claims to Manually Review', value: summary.outlierCount, color: 'warning', labelPrefix: '', labelSuffix: '', isTwoDecimal: false }
        ];

        let patterns = [...list];

        // Filter based on tabs
        if (tabIndex === 0) { // All PATTERN
            patterns = patterns.filter(c => c.severity !== 'Noise'); // Assuming Noise is a severity or similar
        } else if (tabIndex === 1) { // High Severity
            patterns = patterns.filter(c => c.severity === 'High');
        } else if (tabIndex === 2) { // Full Denial
            patterns = patterns.filter(c => c.denialRate === 1.0);
        } else if (tabIndex === 3) { // Outer & Noise
            patterns = patterns.filter(c => c.severity === 'Noise');
        }

        // Sort
        patterns.sort((a, b) => {
            if (sortBy === 'varianceRatioHighToLow') {
                return (b.avgVarianceRatio || 0) - (a.avgVarianceRatio || 0);
            } else if (sortBy === 'claimCountHighToLow') {
                return (b.claimCount || 0) - (a.claimCount || 0);
            } else if (sortBy === 'denialRateHighToLow') {
                return (b.denialRate || 0) - (a.denialRate || 0);
            }
            return 0;
        });

        return { stats, patterns, totalClaims: summary.totalClaims };
    }, [summary, list, tabIndex, sortBy]);

    const toggleDetails = (id: string | number) => {
        setExpandedDetails(expandedDetails === id ? null : id);
    };

    const toggleClaims = (id: string | number) => {
        if (expandedClaims !== id) {
            if (!details[id]) {
                dispatch(patternActions.fetchPatternClaims(id));
            }
            setExpandedClaims(id);
        } else {
            setExpandedClaims(null);
        }
    };

    const renderBatchProfile = (batchProfile: any) => {
        // Since we don't have an API for batch profile yet, we might need to handle it gracefully
        if (!batchProfile) return (
            <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #eff2f5', mt: 3 }}>
                <Typography variant="body1" color="textSecondary">Data profile information not available for current selection.</Typography>
            </Paper>
        );

        const profile = batchProfile || {};
        const totalClaims = profile.total_claims || summary.totalClaims;
        
        return (
            <Box className={classes.batchProfileRoot}>
                <Typography className={classes.batchHeader}>BATCH DATA PROFILE</Typography>
                <Box className={classes.batchCard}>
                    <Box className={classes.batchCardHeader} onClick={() => setBatchExpanded(!batchExpanded)}>
                        <Typography className={classes.batchHeaderText}>
                            {batchExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                            Click to expand — {totalClaims} claims profiled
                        </Typography>
                    </Box>
                    <Collapse in={batchExpanded}>
                        <Box className={classes.batchContent}>
                            <Grid container spacing={5}>
                                <Grid item xs={6}>
                                    <Box className={classes.profileCol}>
                                        <Box className={classes.profileRow}>
                                            <Typography className={classes.profileLabel}>Circuit breaker rate</Typography>
                                            <Typography className={classes.profileValue}>{profile.circuit_breaker_rate || 0}%</Typography>
                                        </Box>
                                        <Box className={classes.profileRow}>
                                            <Typography className={classes.profileLabel}>Denial rate</Typography>
                                            <Typography className={classes.profileValue}>{profile.denial_rate || 0}%</Typography>
                                        </Box>
                                        <Box className={classes.profileRow}>
                                            <Typography className={classes.profileLabel}>Negative variance (overpay)</Typography>
                                            <Typography className={classes.profileValue}>{profile.pct_negative_variance || 0}%</Typography>
                                        </Box>
                                        <Box className={classes.profileRow}>
                                            <Typography className={classes.profileLabel}>Zero charged amount</Typography>
                                            <Typography className={classes.profileValue}>{profile.pct_zero_charged || 0}%</Typography>
                                        </Box>
                                        <Box className={classes.profileRow}>
                                            <Typography className={classes.profileLabel}>Variance ratio p50</Typography>
                                            <Typography className={classes.profileValue}>{profile.variance_ratio_p50 || 0}</Typography>
                                        </Box>
                                        <Box className={classes.profileRow}>
                                            <Typography className={classes.profileLabel}>Variance ratio p95</Typography>
                                            <Typography className={classes.profileValue}>{profile.variance_ratio_p95 || 0}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box className={classes.profileCol}>
                                        {profile.null_rates && Object.entries(profile.null_rates).map(([label, value]: [string, any]) => (
                                            <Box key={label} className={classes.profileRow}>
                                                <Typography className={classes.profileLabelMid}>{label}</Typography>
                                                <Typography 
                                                    className={classes.profileValueNull}
                                                    style={{ color: value > 50 ? '#f64e60' : '#3f4254' }}
                                                >
                                                    {value}% null
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </Box>
            </Box>
        );
    };


    return (
        <Box className={classes.root}>
            <Helmet title="Pattern Intelligence" />
            
            <Box className={classes.container}>

                <Grid container spacing={2} className={classes.statsContainer}>
                    {loading.summary && !summary.asOf ? (
                        [1, 2, 3, 4].map((i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '10px' }} />
                            </Grid>
                        ))
                    ) : (
                        data.stats.map((stat, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <DataTile 
                                    title={stat.label} 
                                    value={stat.value} 
                                    color={stat.color} 
                                    labelPrefix={stat.labelPrefix} 
                                    labelSuffix={stat.labelSuffix} 
                                    isTwoDecimal={stat.isTwoDecimal}
                                />
                            </Grid>
                        ))
                    )}
                </Grid>

                {/* Tabs */}
                <Box className={classes.tabsWrapper}>
                    <Tabs 
                        value={tabIndex} 
                        onChange={(_, v) => setTabIndex(v)}
                        TabIndicatorProps={{ style: { backgroundColor: '#3699ff', height: '3px' } }}
                    >
                        <Tab label="All PATTERN" className={classes.tab} />
                        {/* <Tab label="High Severity" className={classes.tab} />
                        <Tab label="Full Denial" className={classes.tab} />
                        <Tab label="Outliers & Noise" className={classes.tab} />
                        <Tab label="Data Profile" className={classes.tab} /> */}
                    </Tabs>
                </Box>

                {tabIndex !== 4 && (
                    <>
                        {/* Filter Bar */}
                        <Box className={classes.filterBar}>
                            <TextField
                                select
                                label="Sort by"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className={classes.filterField}
                                variant="outlined"
                                size="small"
                            >
                                <MenuItem value="varianceRatioHighToLow">Variance ratio (high to low)</MenuItem>
                                <MenuItem value="claimCountHighToLow">Claim Count (high to low)</MenuItem>
                                <MenuItem value="denialRateHighToLow">Denial Rate (high to low)</MenuItem>
                            </TextField>
                        </Box>

                        <Box className={classes.patternList}>
                            {loading.list && list.length === 0 ? (
                                [1, 2, 3].map((i) => (
                                    <Skeleton key={i} variant="rectangular" height={150} sx={{ borderRadius: '10px', mb: 2 }} />
                                ))
                            ) : data.patterns.length > 0 ? (
                                data.patterns.map((pattern) => (
                                    <Box key={pattern.patternId} className={classes.patternCard}>
                                        <Box className={classes.cardPadding}>
                                            <Box className={classes.cardHeader}>
                                                <Typography 
                                                    className={classes.patternName}
                                                    onClick={(e) => handlePatternClick(e, pattern.patternName)}
                                                    style={{ 
                                                        textDecoration: 'none', 
                                                        color: 'inherit', 
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px'
                                                    }}
                                                >
                                                    {pattern.patternName}
                                                    <LaunchIcon sx={{ fontSize: '16px', color: '#3699ff', opacity: 0.8 }} />
                                                </Typography>
                                                <Box display="flex" alignItems="center" gap={2}>
                                                    <Box 
                                                        className={classes.severityBadge}
                                                        style={{ 
                                                            backgroundColor: pattern.severity === 'Medium' ? '#fff4cc' : (pattern.severity === 'High' ? '#ffe2e5' : '#e8fff3'),
                                                            color: pattern.severity === 'Medium' ? '#b78103' : (pattern.severity === 'High' ? '#f64e60' : '#13a86e')
                                                        }}
                                                    >
                                                        {pattern.severity}
                                                    </Box>
                                                </Box>
                                            </Box>

                                            <Box className={classes.statRow}>
                                                <Box className={classes.metric}>
                                                    <Typography className={classes.metricLabel}>Variance</Typography>
                                                    <Typography className={classes.metricValue}>{((pattern.avgVarianceRatio || 0) * 100).toFixed(1)}%</Typography>
                                                </Box>
                                                <Box className={classes.metric}>
                                                    <Typography className={classes.metricLabel}>Underpay</Typography>
                                                    <Typography className={classes.metricValue}>{((pattern.avgUnderpayRatio || 0) * 100).toFixed(1)}%</Typography>
                                                </Box>
                                                <Box className={classes.metric}>
                                                    <Typography className={classes.metricLabel}>Denial</Typography>
                                                    <Typography className={classes.metricValue}>{((pattern.denialRate || 0) * 100).toFixed(1)}%</Typography>
                                                </Box>
                                                <Box className={classes.metric}>
                                                    <Typography className={classes.metricLabel}>Claims</Typography>
                                                    <Typography className={classes.metricValue}>{pattern.claimCount}</Typography>
                                                </Box>
                                                <Box className={classes.metric}>
                                                    <Typography className={classes.metricLabel}>Payer</Typography>
                                                    <Typography className={classes.metricValue}>{pattern.dominantPayer || ''}</Typography>
                                                </Box>
                                                <Box className={classes.metric}>
                                                    <Typography className={classes.metricLabel}>Visit</Typography>
                                                    <Typography className={classes.metricValue}>{pattern.dominantVisitType || ''}</Typography>
                                                </Box>
                                            </Box>

                                            <Box className={classes.statRow}>
                                                <Box className={classes.metricSecondary}>
                                                    <Typography className={classes.secondaryLabel}>Persistence</Typography>
                                                    <Typography className={classes.secondaryValue}>{((pattern.persistenceScore || 0) * 100).toFixed(1)}%</Typography>
                                                </Box>
                                                <Box className={classes.metricSecondary}>
                                                    <Typography className={classes.secondaryLabel}>Cohesion</Typography>
                                                    <Typography className={classes.secondaryValue}>{((pattern.cohesionScore || 0) * 100).toFixed(1)}%</Typography>
                                                </Box>
                                                <Box className={classes.metricSecondary}>
                                                    <Typography className={classes.secondaryLabel}>Opportunity</Typography>
                                                    <Typography className={classes.secondaryValue}>${(pattern.totalVarianceOpportunity || 0).toLocaleString()}</Typography>
                                                </Box>
                                            </Box>

                                            <Box 
                                                className={classes.viewDetails} 
                                                onClick={() => toggleDetails(pattern.patternId)}
                                            >
                                                View Details
                                                {expandedDetails === pattern.patternId ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                            </Box>
                                        </Box>

                                        <Collapse in={expandedDetails === pattern.patternId}>
                                            <Box className={classes.expandContent}>
                                                {pattern.summary && (
                                                    <Typography className={classes.descriptionText}>
                                                        {pattern.summary}
                                                    </Typography>
                                                )}

                                                <Box className={classes.chipGroup}>
                                                    {pattern.topCasCodes && (Array.isArray(pattern.topCasCodes) ? pattern.topCasCodes : pattern.topCasCodes.split(',')).map((code: string, i: number) => (
                                                        <Chip 
                                                            key={`cas-${i}`} 
                                                            label={`CAS: ${code.trim()}`} 
                                                            className={classes.customChip}
                                                            style={{ backgroundColor: '#f3f6f9', color: '#181c32' }}
                                                        />
                                                    ))}
                                                    {pattern.activeServiceGroups && (Array.isArray(pattern.activeServiceGroups) ? pattern.activeServiceGroups : pattern.activeServiceGroups.split(',')).map((group: string, i: number) => (
                                                        <Chip 
                                                            key={`group-${i}`} 
                                                            label={group.trim()} 
                                                            className={classes.customChip}
                                                            style={{ backgroundColor: '#f3f6f9', color: '#7e8299' }}
                                                        />
                                                    ))}
                                                </Box>

                                                {/* <Box 
                                                    className={classes.claimNumbersToggle}
                                                    onClick={() => toggleClaims(pattern.patternId)}
                                                >
                                                    {expandedClaims === pattern.patternId ? 'Hide' : 'Show'} {pattern.claimCount} claim IDs
                                                    {expandedClaims === pattern.patternId ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                                </Box>

                                                <Collapse in={expandedClaims === pattern.patternId}>
                                                    <Box className={classes.claimNumbersGrid}>
                                                        {loading.details && !details[pattern.patternId] ? (
                                                            <Box display="flex" justifyContent="center" p={2}>
                                                                <CircularProgress size={24} />
                                                            </Box>
                                                        ) : (
                                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))', gap: '10px 15px', p: 1 }}>
                                                                {details[pattern.patternId]?.patternClaims?.map((claim: any) => (
                                                                    <Link
                                                                        key={claim.claimAnalysisId}
                                                                        to={`/cms/claimAnalysis/view/${claim.claimAnalysisId}`}
                                                                        style={{ 
                                                                            color: '#7e8299', 
                                                                            fontSize: '14px',
                                                                            fontWeight: 500,
                                                                            textDecoration: 'none',
                                                                            display: 'block'
                                                                        }}
                                                                    >
                                                                        {claim.claimNumber}
                                                                    </Link>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Collapse> */}
                                            </Box>
                                        </Collapse>
                                    </Box>
                                ))
                            ) : (
                                <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #eff2f5', mt: 3 }}>
                                    <Typography variant="body1" color="textSecondary">No patterns found for this selection.</Typography>
                                </Paper>
                            )}
                        </Box>
                    </>
                )}

                {tabIndex === 4 && renderBatchProfile(null)}
            </Box>
        </Box>
    );
};

export default PatternIntelligencePage;
